import { environment } from 'environments/environment';
import apiService from 'services/ApiService';
import { logger } from 'utils/logger';

// Create a service-specific logger
const log = logger.getLogger('ApiKeysService');

const baseUrl = environment.services.account.baseUrl;
const accountsUrl = baseUrl + environment.services.account.basePath;

export interface ApiKeyAccount {
  id: number;
  name: string;
}

export interface ApiKey {
  id: number;
  active: boolean;
  name: string;
  createDate: string;
  updateDate: string;
  createdBy: string;
  updatedBy: string;
  account: ApiKeyAccount;
  token?: string;
  roboticUserId: number;
}

export interface ApiKeysFilter {
  accountId?: number;
  name?: string;
}

export interface ApiKeysParams {
  page: number;
  size: number;
  sort?: string;
  direction?: 'asc' | 'desc';
  filters?: ApiKeysFilter;
}

export interface CreateApiKeyPayload {
  name: string;
  accountId: number;
}

export interface UpdateApiKeyStatusPayload {
  id: number;
  active: boolean;
}

class ApiKeysService {
  async getApiKeys(params: ApiKeysParams): Promise<{ data: ApiKey[], count: number }> {
    try {
      let apiUrl = `${accountsUrl}/api-keys/?page=${params.page}&size=${params.size}`;

      if (params.sort) {
        const sortFields = params.sort.split(',');
        sortFields.forEach(field => {
          apiUrl += `&sort=${field.trim()}`;
        });
        if (params.direction) {
          apiUrl += `&direction=${params.direction}`;
        }
      }

      if (params.filters) {
        if (params.filters.accountId) {
          apiUrl += `&accountId=${params.filters.accountId}`;
        }

        if (params.filters.name && params.filters.name.trim() !== '') {
          apiUrl += `&search=${encodeURIComponent(params.filters.name.trim())}`;
        }
      }

      log.log('Calling API with URL:', apiUrl);

      const response = await apiService.makeRequest('get', apiUrl);
      const responseData = response.data;

      let total = 0;
      if (responseData && typeof responseData === 'object') {
        total = responseData.count || responseData.totalCount || responseData.total || 0;
      }

      let apiKeysArray = [];

      if (Array.isArray(responseData)) {
        apiKeysArray = responseData;
        total = response?.count || responseData.length;
      } else if (responseData && typeof responseData === 'object') {
        apiKeysArray = responseData.data || responseData.results || responseData.items || responseData.content || [];

        if (!Array.isArray(apiKeysArray) && responseData.id) {
          apiKeysArray = [responseData];
        }
      }

      if (!Array.isArray(apiKeysArray)) {
        throw new Error('Unexpected API response format');
      }

      return {
        data: apiKeysArray,
        count: total
      };
    } catch (error) {
      log.error('Error fetching API keys:', error);
      throw error;
    }
  }

  async getApiKey(id: number): Promise<ApiKey> {
    try {
      const response = await apiService.makeRequest('get', `${accountsUrl}/api-keys/${id}`);
      return response;
    } catch (error) {
      log.error(`Error fetching API key with ID ${id}:`, error);
      throw error;
    }
  }

  async createApiKey(payload: CreateApiKeyPayload): Promise<ApiKey> {
    try {
      const response = await apiService.makeRequest('post', `${accountsUrl}/api-keys/`, payload);

      // Invalidate cache for API keys list
      apiService.invalidateCacheByPattern('/api-keys');

      return response;
    } catch (error) {
      log.error('Error creating API key:', error);
      throw error;
    }
  }

  async updateApiKeyStatus(payload: UpdateApiKeyStatusPayload): Promise<ApiKey> {
    try {
      const response = await apiService.makeRequest('put', `${accountsUrl}/api-keys/${payload.id}/status`, payload);

      // Invalidate cache for API keys list
      apiService.invalidateCacheByPattern('/api-keys');

      return response;
    } catch (error) {
      log.error(`Error updating API key status for ID ${payload.id}:`, error);
      throw error;
    }
  }
}

export const apiKeysService = new ApiKeysService();
export default apiKeysService;
